import type { PersonalRosterEntry } from "@app/lib/api/wiki/compile";
import { useSearchParam } from "@app/lib/platform";
import { usePeople } from "@app/lib/swr/people";
import { useSkills } from "@app/lib/swr/skill_configurations";
import { useWikiPage } from "@app/lib/swr/wiki";
import { getPeopleRoute } from "@app/lib/utils/router";
import type {
  DirectoryAgentType,
  DirectoryHumanType,
  PersonPresence,
} from "@app/types/api/people";
import type { AgentExternalExecutionMode } from "@app/types/assistant/agent";
import { agentDisplayName } from "@app/types/assistant/agent";
import type { LightWorkspaceType } from "@app/types/user";
import { useMemo } from "react";

export const contactsViews = [
  { id: "people", label: "People" },
  { id: "agents", label: "Agents" },
  { id: "skills", label: "Skills" },
] as const;

export type ContactsView = (typeof contactsViews)[number]["id"];

export function isContactsView(value: unknown): value is ContactsView {
  return contactsViews.some((view) => view.id === value);
}

export const presenceLabel: Record<PersonPresence, string> = {
  today: "Active today",
  this_week: "Active this week",
  away: "Away",
  never: "Never signed in",
};

export const executionModeLabel: Record<AgentExternalExecutionMode, string> = {
  managed: "Memoair-hosted",
  connected: "Claude Code on the web",
  local: "Claude Code on a laptop",
};

export function peopleAgentMatchesSearch(
  agent: DirectoryAgentType & { displayName?: string | null },
  needle: string
): boolean {
  const n = needle.trim().toLowerCase();
  if (n === "") {
    return true;
  }
  return (
    agentDisplayName(agent).toLowerCase().includes(n) ||
    agent.name.toLowerCase().includes(n) ||
    agent.description.toLowerCase().includes(n)
  );
}

/**
 * What the Contacts panes have selected, kept in the URL so the list, the
 * main pane and the browser's back button agree. A workspace member and an
 * agent or skill are named by `id`; a contact known only from the wiki is
 * named by its page `slug`, and so is each page of the viewer's own wiki.
 * `tab` picks a view of someone else's card (their memory map) without
 * changing who is selected.
 */
export interface ContactsSelection {
  view: ContactsView;
  id: string | null;
  slug: string | null;
  tab: string | null;
}

export function useContactsSelection(): ContactsSelection {
  const view = useSearchParam("view");
  return {
    view: isContactsView(view) ? view : "people",
    id: useSearchParam("id"),
    slug: useSearchParam("slug"),
    tab: useSearchParam("tab"),
  };
}

export function contactsHref(
  workspaceId: string,
  selection: Partial<ContactsSelection> & { view: ContactsView }
): string {
  const params = new URLSearchParams({ view: selection.view });
  if (selection.id) {
    params.set("id", selection.id);
  }
  if (selection.slug) {
    params.set("slug", selection.slug);
  }
  if (selection.tab) {
    params.set("tab", selection.tab);
  }
  return `${getPeopleRoute(workspaceId)}?${params.toString()}`;
}

export type DirectoryPerson =
  | {
      kind: "member";
      key: string;
      name: string;
      member: DirectoryHumanType;
      // The viewer's own wiki page on this colleague, when there is one.
      wiki: PersonalRosterEntry | null;
    }
  | {
      kind: "contact";
      key: string;
      name: string;
      wiki: PersonalRosterEntry;
    };

function ownProfilePage(member: DirectoryHumanType): PersonalRosterEntry {
  return {
    entitySId: "",
    slug: "profile",
    displayName: member.fullName,
    role: null,
    company: null,
    status: null,
    photoUrl: null,
    emails: [member.email.toLowerCase()],
  };
}

/**
 * Workspace members first, then everyone the viewer's wiki has a page for. A
 * member and a page are the same person when the page carries the member's
 * address, so a colleague who also mails the viewer appears once, as a member
 * with their page attached.
 */
export function useDirectoryPeople(owner: LightWorkspaceType) {
  const { humans, agents, isPeopleLoading } = usePeople({ owner });
  const { peopleRoster, isPageLoading } = useWikiPage({
    owner,
    slug: "people",
  });
  const { skills, isSkillsLoading } = useSkills({ owner, status: "active" });

  const people = useMemo((): DirectoryPerson[] => {
    const roster = peopleRoster ?? [];
    const pageByEmail = new Map(
      roster.flatMap((entry) =>
        (entry.emails ?? []).map((email) => [email, entry] as const)
      )
    );
    const members = humans.map((member): DirectoryPerson => {
      // The viewer has no person page about themselves; what the wiki knows
      // about them is their own Profile.
      const wiki = member.isSelf
        ? ownProfilePage(member)
        : (pageByEmail.get(member.email.toLowerCase()) ?? null);
      return {
        kind: "member",
        key: `member:${member.sId}`,
        name: member.isSelf ? `${member.fullName} (you)` : member.fullName,
        member,
        wiki,
      };
    });
    const memberSlugs = new Set(
      members.flatMap((person) => (person.wiki ? [person.wiki.slug] : []))
    );
    const contacts = roster
      .filter((entry) => !memberSlugs.has(entry.slug))
      .map(
        (entry): DirectoryPerson => ({
          kind: "contact",
          key: `contact:${entry.slug}`,
          name: entry.displayName,
          wiki: entry,
        })
      );
    return [...members, ...contacts];
  }, [humans, peopleRoster]);

  return {
    people,
    agents,
    skills,
    isPeopleLoading: isPeopleLoading || isPageLoading,
    isAgentsLoading: isPeopleLoading,
    isSkillsLoading,
  };
}

export function selectedPerson(
  people: DirectoryPerson[],
  selection: ContactsSelection
): DirectoryPerson | null {
  if (selection.id) {
    return (
      people.find(
        (person) =>
          person.kind === "member" && person.member.sId === selection.id
      ) ?? null
    );
  }
  const bySlug = selection.slug
    ? people.find((person) => person.wiki?.slug === selection.slug)
    : undefined;
  if (bySlug) {
    return bySlug;
  }
  // Nothing picked yet, or one of the viewer's own wiki pages: both open the
  // viewer's own card.
  return (
    people.find((person) => person.kind === "member" && person.member.isSelf) ??
    null
  );
}

export function personHref(
  workspaceId: string,
  person: DirectoryPerson
): string {
  return person.kind === "member"
    ? contactsHref(workspaceId, { view: "people", id: person.member.sId })
    : contactsHref(workspaceId, { view: "people", slug: person.wiki.slug });
}
